import {
  Component,
  AfterViewInit,
  OnDestroy,
  Input,
  inject,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { GlobalFilterComponent } from '../../global-components/global-filter/global-filter.component';
import { HttpClient } from '@angular/common/http';
import * as echarts from 'echarts';
import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { ThemeService } from '../../shared-services/theme.service';
import { Router } from '@angular/router';
import { Subject, Subscription, takeUntil } from 'rxjs';

interface RegionBounds {
  center: [number, number];
  zoom: number;
}

import { CapitalCasePipe } from '../../pipes/capital-case.pipe';

@Component({
  selector: 'app-world-map',
  standalone: true,
  imports: [CommonModule, CapitalCasePipe, GlobalFilterComponent],
  templateUrl: './world-map.component.html',
  styleUrls: ['./world-map.component.scss']
})
export class WorldMapComponent implements AfterViewInit, OnDestroy, OnInit {

  @Input() data: any;

  protected chart!: echarts.ECharts;
  protected mapRegistered = false;
  protected geoJson: any = null;
  // dynamic risk config loaded from API response (if present)
  protected riskConfig: any = null;
  // last created bubble data (used to filter on region click)
  protected lastBubbleData: any[] = [];
  protected bubbleSeriesIndex = -1;
  protected unSubscribe$ = new Subject<void>();
  protected resizeListener: (() => void) | null = null;
  protected clickListener: ((params: any) => void) | null = null;
  private themeService = inject(ThemeService);
  private themeSub?: Subscription;
  private lastChartData?: any;

  protected setRiskConfig(cfg: any) {
    this.riskConfig = cfg || null;
  }

  protected loadRiskFromResponse(resp: any) {
    try {
      const first = Array.isArray(resp?.responseList) && resp.responseList[0];
      const candidate = first?.['filter-data']?.[0]?.data?.riskConfig || first?.riskConfig || resp?.riskConfig || resp?.riskLevels;
      if (candidate) {
        this.setRiskConfig(candidate);
        return;
      }
    } catch (_e) {
      // swallow parse errors
    }
    this.riskConfig = null;
  }

  protected defaultColor(score: number): string {
    const map: Record<number, string> = {
      1: '#22c55e',
      2: '#84cc16',
      3: '#eab308',
      4: '#f97316',
      5: '#ef4444',
    };
    return map[Math.round(score)] || '#eab308';
  }

  protected defaultLabel(score: number): string {
    if (score <= 1) return 'Very Low';
    if (score === 2) return 'Low';
    if (score === 3) return 'Medium';
    if (score === 4) return 'High';
    return 'Very High';
  }

  protected getRiskColor(score: number): string {
    if (!this.riskConfig) return this.defaultColor(score);
    if (this.riskConfig.scores && this.riskConfig.scores[Math.round(score)]) {
      return this.riskConfig.scores[Math.round(score)].color;
    }
    if (this.riskConfig.ranges) {
      for (const r of this.riskConfig.ranges) {
        const min = r.min ?? -Infinity;
        const max = r.max ?? Infinity;
        if (score >= min && score <= max) return r.color;
      }
    }
    return this.defaultColor(score);
  }

  protected getRiskLabel(score: number): string {
    if (!this.riskConfig) return this.defaultLabel(score);
    if (this.riskConfig.scores && this.riskConfig.scores[Math.round(score)]) {
      return this.riskConfig.scores[Math.round(score)].label;
    }
    if (this.riskConfig.ranges) {
      for (const r of this.riskConfig.ranges) {
        const min = r.min ?? -Infinity;
        const max = r.max ?? Infinity;
        if (score >= min && score <= max) return r.label;
      }
    }
    return this.defaultLabel(score);
  }

  protected calculateCompositeMineRisk(mine: any): number {
    const repower = mine.repower_score ?? 3;
    const outlook = mine.mine_outlook_score ?? 3;
    const tco = mine.competition_tco ?? 3;
    const satisfaction = mine.satisfaction_score ?? 3;
    const w = this.riskConfig?.weights || { repower: 0.4, outlook: 0.25, tco: 0.2, satisfaction: 0.15 };
    const score = repower * (w.repower ?? 0.4) + outlook * (w.outlook ?? 0.25) + tco * (w.tco ?? 0.2) + satisfaction * (w.satisfaction ?? 0.15);
    return Math.round(Math.min(5, Math.max(1, score)));
  }

  protected getMineRisk(mine: any) {
    const score = mine.repower_score ?? this.calculateCompositeMineRisk(mine);
    return {
      score,
      label: this.getRiskLabel(score),
      color: this.getRiskColor(score),
    };
  }

  protected buildMineRiskFilter(mines: any[]): string[] {
    return Array.from(new Set(mines.map((m) => this.getRiskLabel(m.repower_score ?? this.calculateCompositeMineRisk(m)))));
  }

  protected calculateRegionRisk(mines: any[]): number {
    if (!mines || mines.length === 0) return 3;
    const avg = mines.reduce((s, m) => s + (m.repower_score ?? 3), 0) / mines.length;
    return Math.round(avg);
  }

  protected getBubbleSizeFromRisk(score: number): number {
    const minSize = this.data?.minBubbleSize ?? 10;
    const maxSize = this.data?.maxBubbleSize ?? 36;
    const clamped = Math.max(1, Math.min(5, Math.round(score)));
    return Math.round(minSize + ((clamped - 1) / 4) * (maxSize - minSize));
  }

  // Hard-coded country coordinates for reliable bubble positioning
  protected countryCoordinates: Record<string, [number, number]> = {
    'chile': [-70.6693, -33.4489],
    'peru': [-77.0428, -12.0464],
    'australia': [133.7751, -25.2744],
    'australia (queensland)': [144.9631, -27.4698],
    'china': [104.1954, 35.8617],
    'canada': [-106.3468, 56.1304],
    'finland': [25.7482, 61.9241],
    'south africa': [22.9375, -30.5595],
    'usa': [-95.7129, 37.0902],
    'united states': [-95.7129, 37.0902],
    'mongolia': [103.8467, 46.8625],
    'zambia': [27.8493, -13.1339],
    'mexico': [-102.5528, 23.6345]
  };

  protected readonly regionBounds: Record<string, RegionBounds> = {
    LATAM: { center: [-65, -15], zoom: 1.8 },
    NACA: { center: [-100, 45], zoom: 1.8 },
    AME: { center: [20, 5], zoom: 1.8 },
    APAC: { center: [120, 5], zoom: 1.6 },
    CHINA: { center: [104.2, 35.9], zoom: 2.5 }
  };

  constructor(
    protected configService: ConfigService,
    protected globalFilterService: GlobalFilterService,
    private http: HttpClient,
    private router: Router,
    protected chartFilterHandler: ChartFilterHandlerService,
  ) {}

  ngOnInit(): void {
    // Now this.data is available since @Input is initialized
    this.globalFilterService.filter$
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((filters) => {
        console.log('[WorldMap] Filter received:', { widgetId: this.data?.widgetId, filters });
        
        // Check if this widget should respond to the filter change
        const shouldReload = this.chartFilterHandler.shouldReload(filters, this.data?.widgetId);
        console.log('[WorldMap] shouldReload?', shouldReload, 'filterListener:', filters?.filterListener);
        
        if (shouldReload) {
          // Check if there are actual filter values (requestParams or queryParams)
          const hasFilters = 
            (filters?.requestParams && Object.keys(filters.requestParams).length > 0) ||
            (filters?.queryParams && Object.keys(filters.queryParams).length > 0);
          
          console.log('[WorldMap] hasFilters?', hasFilters, 'requestParams:', filters?.requestParams, 'queryParams:', filters?.queryParams);
          
          // Always reload on shouldReload true, regardless of filter count
          // This allows initial load and filter updates
          if (Object.keys(filters).length > 0 || !this.chart) {
            console.log('[WorldMap] Calling loadChart with filters');
            this.loadChart(filters);
          }
        }
      });
  }

  ngAfterViewInit(): void {
    this.loadChart();

    // Re-render with fresh theme tokens when the user switches theme at runtime.
    // Dispose + re-init so ECharts picks up the updated 'dvm' registered theme.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.lastChartData || !this.chart) return;
      const chartDom = document.getElementById(this.data.widgetId);
      if (!chartDom) return;
      this.chart.dispose();
      this.chart = echarts.init(chartDom, 'dvm');
      this.renderChart(this.lastChartData);
    });
  }

  protected loadChart(filters?: any): void {
    const chartDom = document.getElementById(this.data.widgetId);
    if (!chartDom) return;

    if (!this.chart) {
      this.chart = echarts.init(chartDom, 'dvm');
    }

    this.chart.showLoading();

    if (!this.mapRegistered) {
      this.http.get('assets/world.json').subscribe({
        next: (worldJson: any) => {
          this.geoJson = worldJson;
          echarts.registerMap('world', worldJson);
          this.mapRegistered = true;
          this.fetchData(filters);
        },
        error: () => {
          this.chart.hideLoading();
          console.error('Failed to load world map GeoJSON');
        }
      });
    } else {
      this.fetchData(filters);
    }
  }

  protected fetchData(filters?: any): void {
    this.configService.getConfig(this.data.apiUrl, this.data, filters, this.data.method).subscribe({
      next: (res) => {
        // load risk config from response into local config
        try {
          this.loadRiskFromResponse(res);
        } catch (_e) {
          // ignore
        }
        this.renderChart(res);
      },
      error: () => {
        this.chart.hideLoading();
      }
    });
  }

  protected getCountryCenter(countryName: string): [number, number] | null {
    const norm = countryName.toLowerCase().trim();
    
    // Check hardcoded coordinates first
    if (this.countryCoordinates[norm]) {
      return this.countryCoordinates[norm];
    }

    // Try partial match
    for (const key of Object.keys(this.countryCoordinates)) {
      if (key.includes(norm) || norm.includes(key)) {
        return this.countryCoordinates[key];
      }
    }

    return null;
  }

  protected renderChart(chartData?: any): void {
    if (!this.data) return;

    if (chartData) this.lastChartData = chartData;

    const theme = this.themeService.resolve(this.data?.themeOverrides);
    const isStatic = this.data?.static === true || this.data?.isStatic === true;

    // Build series array
    const series: any[] = [];

    // Add bubbles if enabled
    const bubbleEnabled = this.data?.bubble === true;
    console.log('WorldMap: bubble enabled?', bubbleEnabled);

    if (bubbleEnabled && chartData?.responseList) {
      const bubbleValueKey = this.data?.bubbleValueKey || 'repower_score';
      const bubbleLabel = this.data?.bubbleLabel || 'mine';
      const bubbleCountryKey = this.data?.bubbleCountryKey || 'country';
      const bubbleColor = this.data?.bubbleColor || '#ff5722';
      const bubbleScatterRadius = this.data?.bubbleScatterRadius ?? 5.5; // degrees

      const bubbleData: any[] = [];
      const countryBubbleIndex: Record<string, number> = {};

      // Extract data from responseList -> ui-data -> data
      for (const resp of chartData.responseList) {
        const uiDataList = resp['ui-data'] || [];
        
          for (const uiData of uiDataList) {
            const rows = uiData.data || [];

          for (const row of rows) {
            const name = row[bubbleLabel];
            const country = row[bubbleCountryKey];
            // Determine which key represents risk/value for sizing & coloring.
            const riskKey = this.data?.riskKey || this.data?.bubbleValueKey || bubbleValueKey;
            const rawValue = row[riskKey];
            const value = typeof rawValue !== 'undefined' && rawValue !== null ? rawValue : (row[bubbleValueKey] ?? 1);

            if (!name || !country) continue;

            const center = this.getCountryCenter(country);
            if (center) {
              // Scatter bubbles within the same country with smaller, bounded offsets
              const normCountry = country.toLowerCase().trim();
              const idx = countryBubbleIndex[normCountry] || 0;
              countryBubbleIndex[normCountry] = idx + 1;

              // Use configurable scatter radius and bound offsets
              const scatterX = (idx % 3) - 1; // -1, 0, 1
              const scatterY = Math.floor(idx * 3) - 0.5; // -0.5, 0.5, 1.5, etc.

              const offsetX = Math.max(-bubbleScatterRadius, Math.min(bubbleScatterRadius, scatterX * 0.8));
              const offsetY = Math.max(-bubbleScatterRadius, Math.min(bubbleScatterRadius, scatterY * 0.8));

              // compute risk metadata and color/size using inlined helpers
              const riskMeta = this.getMineRisk({ repower_score: value });
              const color = riskMeta.color || bubbleColor;
              const size = this.getBubbleSizeFromRisk(riskMeta.score);

              bubbleData.push({
                name: name,
                value: [center[0] + offsetX, center[1] + offsetY, value, riskMeta.score],
                itemStyle: { color },
                __risk: riskMeta,
                __countryNorm: normCountry,
                __countryRaw: country
              });
              
            } else {
              // missing coordinates
            }
          }
        }
      }

      

      if (bubbleData.length > 0) {
        // remember bubble data and the index where the series will be pushed
        this.bubbleSeriesIndex = series.length;
        this.lastBubbleData = bubbleData;
        series.push({
          name: this.data?.bubbleTitle || 'Mines',
          type: 'scatter',
          coordinateSystem: 'geo',
          data: bubbleData,
          symbolSize: (val: any) => {
            // expect val = [lon, lat, originalValue, riskScore]
            const score = Array.isArray(val) ? (val[3] ?? val[2] ?? 3) : (val ?? 3);
            return this.getBubbleSizeFromRisk(score);
          },
          label: {
            show: false
          },
          itemStyle: {
            color: bubbleColor
          },
          zlevel: 1
        });
      }
    }
    // Build regions array from data.regionMappings + data.regionColors so each country can be colored
    const regionMappings = this.data?.regionMappings || {};
    const regionColors = this.data?.regionColors || {};

    // Create a flat list of region entries for ECharts 'regions' option
    const regionsArray: any[] = [];
    for (const regionKey of Object.keys(regionMappings)) {
      const countries: string[] = regionMappings[regionKey] || [];
      const color = regionColors[regionKey] || '#f4a582';
      for (const countryName of countries) {
        regionsArray.push({
          name: countryName,
          itemStyle: { areaColor: color, borderColor: '#ffffff', borderWidth: 0.8 },
          emphasis: { itemStyle: { areaColor: color, borderColor: '#ffffff', borderWidth: 0.8 }, label: { show: true } }
        });
      }
    }

    const option: any = {
      tooltip: {
        trigger: 'item',
        backgroundColor: theme.tooltip?.bg || '#fff',
        borderColor: theme.tooltip?.borderColor || '#ccc',
        textStyle: {
          color: theme.tooltip?.textColor || '#333',
          fontSize: theme.tooltip?.fontSize || 13,
        },
        formatter: (params: any) => {
          if (params.seriesType === 'effectScatter') {
            const val = Array.isArray(params.value) ? params.value[2] : params.value;
            return `<strong>${params.name}</strong><br/>Score: ${val}`;
          }
          return `<strong>${params.name}</strong>`;
        }
      },
      geo: {
        map: 'world',
        roam: !isStatic,
        top: 0,
        bottom: 100,
        left: 0,
        right: 0,
        itemStyle: {
          areaColor: '#9cadb8',
          borderColor: '#ffffff',
          borderWidth: 0.8
        },
        emphasis: {
          itemStyle: {
            areaColor: '#f4a582'
          },
          label: {
            show: true
          }
        },
        regions: regionsArray
      },
      series: series
    };

    applyRoleVisibilityToOption(option, this.data);

    this.chart.setOption(option, true);
    this.chart.hideLoading();

    // Zoom to selectedMine if present
    if (this.data?.selectedMine) {
      setTimeout(() => this.zoomToSelectedMine(this.data.selectedMine), 500);
    }

    // Clean up old event listeners before registering new ones
    this.chart.off('click');
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }

    // Click handler: zoom to region on map click, or zoom+filter on bubble click
    this.clickListener = (params: any) => {
      // Map region clicked
      if (params.componentType === 'geo') {
        const region = this.getRegionForCountry(params.name);
        if (region) {
          this.zoomToRegion(region);
        }
      }

      // Bubble clicked (series)
      if (params.seriesType === 'scatter' || params.seriesType === 'effectScatter' || params.componentType === 'series') {
        try {
          const point = params.data;
          if (!point || !this.lastBubbleData) return;

          // Find matching bubble by name or coordinates
          const match = this.lastBubbleData.find(b => {
            if (b.name && point.name && b.name === point.name) return true;
            const bv = Array.isArray(b.value) ? b.value : null;
            const pv = Array.isArray(point.value) ? point.value : (point && point.value && Array.isArray(point.value.value) ? point.value.value : null);
            if (bv && pv) return bv[0] === pv[0] && bv[1] === pv[1];
            return false;
          });

          if (match) {
            // Check if navigation is configured
            if (this.data?.customPayload?.navigateTo) {
              this.router.navigate([this.data.customPayload.navigateTo], {
                state: { selectedMine: match }
              });
              return;
            }

            const countryRaw = match.__countryRaw || '';
            const region = this.getRegionForCountry(countryRaw);
            if (region) {
              this.zoomToRegion(region);
              setTimeout(() => this.filterSeriesTo([match]), 350);
            } else {
              this.zoomToBubble(point);
            }
          } else {
            this.zoomToBubble(point);
          }
        } catch (e) {
          console.warn('WorldMap: failed handling bubble click', e);
        }
      }
    };
    this.chart.on('click', this.clickListener);

    // Register resize listener
    this.resizeListener = () => this.chart?.resize();
    window.addEventListener('resize', this.resizeListener);
  }

  protected getRegionForCountry(countryName: string): string | null {
    const regionMappings = this.data?.regionMappings || {};
    for (const regionKey of Object.keys(regionMappings)) {
      const countries: string[] = regionMappings[regionKey] || [];
      if (countries.some(c => c.toLowerCase().trim() === countryName.toLowerCase().trim())) {
        return regionKey;
      }
    }
    return null;
  }

  protected zoomToRegion(regionName: string): void {
    if (!this.chart || !this.regionBounds[regionName]) return;

    const bounds = this.regionBounds[regionName];

    // Build regions array for selected region only
    const regionMappings = this.data?.regionMappings || {};
    const regionColors = this.data?.regionColors || {};
    const selectedCountries = regionMappings[regionName] || [];
    const selectedColor = regionColors[regionName] || '#f4a582';
    const regionsArray = selectedCountries.map((countryName: string) => ({
      name: countryName,
      itemStyle: { areaColor: selectedColor, borderColor: '#333' },
      emphasis: { itemStyle: { areaColor: selectedColor }, label: { show: true } }
    }));

    this.chart.setOption(
      {
        geo: {
          center: bounds.center,   // 🎯 perfectly centered
          zoom: bounds.zoom,       // 🔍 fitted zoom
          roam: false,
          itemStyle: {
            areaColor: 'transparent',
            borderColor: '#ffffff',
            borderWidth: 0.8
          },
          regions: regionsArray
        }
      },
      {
        notMerge: true,
        lazyUpdate: false
      }
    );

    // Filter bubble series to only show mines belonging to the selected region
    try {
      const regionMappings = this.data?.regionMappings || {};
      const selectedCountries: string[] = regionMappings[regionName] || [];
      const selectedNorm = selectedCountries.map(c => c.toLowerCase().trim());

      const filtered = (this.lastBubbleData || []).filter(b => selectedNorm.includes((b.__countryNorm || '').toLowerCase().trim()));
      this.filterSeriesTo(filtered);
    } catch (e) {
      console.warn('WorldMap: failed to filter bubbles for region', e);
    }

    // ✅ IMPORTANT: resize after animation so it fits screen
    setTimeout(() => {
      this.chart.resize();
    }, 300);
  }

  protected zoomToBubble(point: any): void {
    if (!this.chart || !point) return;
    const coords = this.getCoordsFromPoint(point);
    if (!coords) return;
    const [lon, lat] = coords;
    const zoomLevel = this.data?.bubbleClickZoom ?? 6;

    try {
      this.chart.setOption({ geo: { center: [lon, lat], zoom: zoomLevel, roam: false } }, { notMerge: false, lazyUpdate: false });
      // find matching bubble(s)
      const filtered = (this.lastBubbleData || []).filter(b => {
        if (b.name && point.name && b.name === point.name) return true;
        const bv = Array.isArray(b.value) ? b.value : null;
        if (bv) return bv[0] === lon && bv[1] === lat;
        return false;
      });
      this.filterSeriesTo(filtered);
      setTimeout(() => this.chart.resize(), 300);
    } catch (e) {
      console.warn('WorldMap: zoomToBubble failed', e);
    }
  }

  protected getCoordsFromPoint(point: any): [number, number] | null {
    if (!point) return null;
    const v = Array.isArray(point.value) ? point.value : (point && point.value && Array.isArray(point.value.value) ? point.value.value : null);
    if (!v || v.length < 2) return null;
    return [v[0], v[1]];
  }

  protected filterSeriesTo(data: any[]) {
    const currentOpt: any = this.chart.getOption();
    if (currentOpt && Array.isArray(currentOpt.series) && this.bubbleSeriesIndex >= 0 && this.bubbleSeriesIndex < currentOpt.series.length) {
      currentOpt.series[this.bubbleSeriesIndex].data = data;
      this.chart.setOption(currentOpt, true);
    } else {
      this.chart.setOption({ series: [{ data }] }, { notMerge: false, lazyUpdate: false });
    }
  }

  protected zoomToSelectedMine(selectedMine: any): void {
    if (!this.chart || !selectedMine) return;
    const coords = this.getCoordsFromPoint(selectedMine);
    if (!coords) return;
    const [lon, lat] = coords;
    const zoomLevel = this.data?.bubbleClickZoom ?? 6;

    try {
      this.chart.setOption({ geo: { center: [lon, lat], zoom: zoomLevel, roam: false } }, { notMerge: false, lazyUpdate: false });
      // Filter to show only the selected mine
      this.filterSeriesTo([selectedMine]);
      setTimeout(() => this.chart.resize(), 300);
    } catch (e) {
      console.warn('WorldMap: zoomToSelectedMine failed', e);
    }
  }

  ngOnDestroy(): void {
    // Clean up event listeners
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
      this.resizeListener = null;
    }
    if (this.clickListener && this.chart) {
      this.chart.off('click', this.clickListener);
      this.clickListener = null;
    }
    
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
    this.themeSub?.unsubscribe();
    this.chart?.dispose();
  }
}
